import {
  createElement,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { DayProps } from "react-day-picker";
import {
  fromJsDate,
  isLocalDateAfter,
  isLocalDateBefore,
  isWeekend,
  toJsDate,
  today,
} from "@freightsimple/time/helpers";
import type { LocalDate } from "@freightsimple/time/branded-types";
import { Tooltip } from "../../Feedback/Tooltip";
import { T } from "../../Typography/T";
import { Calendar } from "../Internal/Calendar";

/** A holiday to highlight (gold) and name in the hover tooltip. */
export interface PickupDateHoliday {
  date: LocalDate;
  name: ReactNode;
}

/**
 * Messages shown on hover (and, for the chosen day, below the grid) explaining
 * why a date is unavailable or noteworthy. Any omitted message shows nothing.
 */
interface PickupDateTooltips {
  inThePast?: ReactNode;
  beforeEarliest?: ReactNode;
  beyondLatest?: ReactNode;
  weekend?: ReactNode;
  today?: ReactNode;
}

const DEFAULT_TOOLTIPS: PickupDateTooltips = {
  inThePast: "❌ This date is in the past",
  weekend: "❌ Pickup date can not be on a weekend",
  beyondLatest:
    "⚠️ FreightSimple only supports booking shipments within the next month",
  today:
    "⚠️ For same-day pickups, carriers typically need at least 3 hours' notice. If they can't make it, they'll pick up on the next business day.",
};

function isWeekendJsDate(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

interface PickupDatePickerProps {
  value?: LocalDate | undefined;
  onChange?: ((date: LocalDate) => void) | undefined;
  /**
   * The day marked as "today" and the default earliest selectable date.
   * Defaults to the actual today; pass a fixed date in stories/tests for
   * determinism.
   */
  referenceDate?: LocalDate | undefined;
  /** Earliest selectable date (inclusive). Defaults to `referenceDate`. */
  earliestDate?: LocalDate | undefined;
  /** Latest selectable date (inclusive). Omit for no upper bound. */
  latestDate?: LocalDate | undefined;
  /** Extra dates to grey out. Composes with the weekend and window rules. */
  disabledDate?: ((date: LocalDate) => boolean) | undefined;
  /** Permit weekend selection. Default false — pickups run on business days. */
  allowWeekends?: boolean | undefined;
  /**
   * Months shown side by side. Left unset it's responsive: two when there's
   * room, one when the available width would force them to wrap onto two rows
   * (a phone). Pass a number to pin it.
   */
  numberOfMonths?: number | undefined;
  /**
   * Holidays to highlight in gold and name on hover. A holiday inside the
   * booking window stays selectable; one outside it is greyed out like any
   * other unavailable day. ui-core stays holiday-library-agnostic — the app
   * supplies the dates and names.
   */
  holidays?: readonly PickupDateHoliday[] | undefined;
  /** Override the default hover/description messages. */
  tooltips?: PickupDateTooltips | undefined;
}

/**
 * The large, inline pickup-date calendar from the customer dashboard's quoting
 * flow — a two-month, single-select `Calendar` in its "booking" skin with the
 * pickup rules baked in: business days only, nothing before the earliest day,
 * nothing past the booking window. Hovering an unavailable (or noteworthy) day
 * explains why — in the past, on a weekend, beyond the window, a holiday, or
 * today — and the chosen day's note is echoed below the grid. Values are
 * `LocalDate`s.
 */
export function PickupDatePicker({
  value,
  onChange,
  referenceDate,
  earliestDate,
  latestDate,
  disabledDate,
  allowWeekends = false,
  numberOfMonths,
  holidays,
  tooltips,
}: PickupDatePickerProps) {
  const reference = referenceDate ?? today();
  const earliest = earliestDate ?? reference;
  const messages = { ...DEFAULT_TOOLTIPS, ...tooltips };
  const holidayName = (date: LocalDate): ReactNode | undefined =>
    holidays?.find((h) => h.date === date)?.name;

  const isUnavailable = (date: LocalDate): boolean =>
    (!allowWeekends && isWeekend(date)) ||
    isLocalDateBefore(date, earliest) ||
    (latestDate !== undefined && isLocalDateAfter(date, latestDate)) ||
    (disabledDate?.(date) ?? false);

  // Why a date is unavailable or noteworthy — shown on hover and, for the
  // chosen day, below the grid. Order mirrors the dashboard picker.
  const reasonFor = (date: LocalDate): ReactNode | undefined => {
    if (isLocalDateBefore(date, reference)) return messages.inThePast;
    if (isLocalDateBefore(date, earliest)) return messages.beforeEarliest;
    if (latestDate !== undefined && isLocalDateAfter(date, latestDate))
      return messages.beyondLatest;
    if (!allowWeekends && isWeekend(date)) return messages.weekend;
    const name = holidayName(date);
    if (name !== undefined) return name;
    if (date === reference) return messages.today;
    return undefined;
  };

  const [chosenReason, setChosenReason] = useState<ReactNode>(() =>
    value !== undefined ? reasonFor(value) : undefined,
  );

  // Two months side by side need ~620px; below that they wrap onto two rows,
  // which reads worse than a single month with the arrows to page forward. We
  // measure the available width (a full-width wrapper, not the shrink-wrapped
  // calendar) and drop to one month under the threshold. Until measured we
  // assume there's room, so a wide screen never flickers through one month.
  const availableRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState<number | undefined>(
    undefined,
  );
  useEffect(() => {
    const element = availableRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width !== undefined) setAvailableWidth(width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const TWO_MONTH_MIN_WIDTH = 620;
  const responsiveMonths =
    availableWidth !== undefined && availableWidth < TWO_MONTH_MIN_WIDTH
      ? 1
      : 2;
  const effectiveMonths = numberOfMonths ?? responsiveMonths;

  const modifiers = {
    weekend: isWeekendJsDate,
    holiday: (d: Date) => {
      const iso = fromJsDate(d);
      return holidayName(iso) !== undefined && !isUnavailable(iso);
    },
    holidayDisabled: (d: Date) => {
      const iso = fromJsDate(d);
      return holidayName(iso) !== undefined && isUnavailable(iso);
    },
  };
  const modifiersClassNames = {
    weekend: "bg-muted",
    holiday:
      "[&_button]:bg-gold! [&_button]:font-medium! [&_button]:text-on-bright! [&_button]:rounded-lg",
    holidayDisabled:
      "[&_button]:bg-gold/15! [&_button]:text-content-faint! [&_button]:rounded-lg",
  };

  // A day cell whose button is wrapped in a tooltip. The tooltip lives INSIDE
  // the <td> (not around it) — wrapping the <td> itself would nest a table cell
  // inside the tooltip's <span>, which is invalid HTML. Disabled days still
  // surface the tooltip because their buttons keep pointer events.
  function PickupDay_({
    day,
    modifiers: _modifiers,
    children,
    ...cellProps
  }: DayProps) {
    const isoDate = fromJsDate(day.date);
    const reason = reasonFor(isoDate);
    // Stable per-day hook for tests — the classNames overrides replace
    // react-day-picker's rdp-* classes, so tests can't target those.
    const cellPropsWithTestId = {
      ...cellProps,
      "data-testid": `calendar-day-${isoDate}`,
    };
    if (reason === undefined) {
      return createElement("td", cellPropsWithTestId, children);
    }
    return createElement(
      "td",
      cellPropsWithTestId,
      // roomy: these are sentences explaining why a day is unavailable, not
      // terse labels. The variant stays `default` because the trigger has to be
      // the day button — info/error render their own icon instead.
      <Tooltip content={reason} side="bottom" roomy>
        {children}
      </Tooltip>,
    );
  }

  return (
    // The outer wrapper fills the available width so we can measure it; the
    // inner one shrink-wraps the calendar so it doesn't stretch to fill, and
    // centres itself in that width — the months need less room than the panel
    // they sit in, so left-aligned they hang off to one side with all the slack
    // on the right.
    <div ref={availableRef} className="w-full">
      <div className="mx-auto w-fit">
        <Calendar
          variant="booking"
          mode="single"
          numberOfMonths={effectiveMonths}
          today={toJsDate(reference)}
          defaultMonth={value ? toJsDate(value) : toJsDate(reference)}
          {...(value ? { selected: toJsDate(value) } : {})}
          disabled={(date: Date) => isUnavailable(fromJsDate(date))}
          modifiers={modifiers}
          modifiersClassNames={modifiersClassNames}
          components={{ Day: PickupDay_ }}
          onSelect={(date: Date | undefined) => {
            if (!date) return;
            const iso = fromJsDate(date);
            if (isUnavailable(iso)) return;
            setChosenReason(reasonFor(iso));
            onChange?.(iso);
          }}
        />
        {chosenReason !== undefined && (
          <div className="mt-4 px-2">
            <T size="sm" color="secondary">
              {chosenReason}
            </T>
          </div>
        )}
      </div>
    </div>
  );
}
